/**
 * The game session: owns the rules engine, the piece meshes, and the flow of a
 * turn (select → move → animate → hand over). It deliberately knows nothing
 * about the DOM; the shell in main.js subscribes through the callbacks in
 * `hooks` and renders the HUD from them.
 */

import { Chess, other } from './chess.js';
import { chooseMove } from './ai.js';
import { createPiece } from '../three/pieces.js';
import { Board, squareToWorld } from '../three/board.js';
import { Animator, resetPose } from '../three/animator.js';
import { POOL_BY_TYPE } from '../audio/soundboard.js';

const GLYPHS = {
  w: { p: '♙', n: '♘', b: '♗', r: '♖', q: '♕', k: '♔' },
  b: { p: '♟', n: '♞', b: '♝', r: '♜', q: '♛', k: '♚' }
};

const HELMET_COUNT = 3;

/** A small seeded PRNG, so one roll at setup fixes the whole deal. */
function mulberry32(seed) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Which helmet each pawn wears, keyed by the square it started on.
 *
 * Dealt rather than rolled per pawn: eight independent rolls over three
 * helmets leaves a rank all of one kind often enough to look like a bug, so
 * each army is handed a 3/3/2 split and the split is shuffled. Keyed on
 * `origin` because that is the one thing about a piece that survives
 * `Session.rebuild` — ids are reissued, and squares change as pawns advance.
 */
function dealHelmets(game) {
  const rand = mulberry32((Math.random() * 0x7fffffff) | 0);
  const helmets = new Map();
  for (const color of ['w', 'b']) {
    const squares = [];
    for (let i = 0; i < 64; i++) {
      const piece = game.board[i];
      if (piece && piece.type === 'p' && piece.color === color) squares.push(i);
    }
    // Eight pawns over three helmets is 3/3/2, and the offset decides which
    // type draws the short straw — without it the same helmet is the rare one
    // in every game ever played, which is not what "random" is being asked for.
    const offset = Math.floor(rand() * HELMET_COUNT);
    const deal = squares.map((_, i) => (i + offset) % HELMET_COUNT);
    for (let i = deal.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [deal[i], deal[j]] = [deal[j], deal[i]];
    }
    squares.forEach((square, i) => helmets.set(square, deal[i]));
  }
  return helmets;
}

export class Session {
  /**
   * @param {SceneView} view
   * @param {object} options { mode: 'ai'|'hotseat'|'remote', difficulty, playerColor }
   * @param {object} hooks   { onTurn, onMove, onCapture, onStatus, onCheck,
   *   onPromotion, onLocalMove } — `onLocalMove` only ever fires in
   *   'remote' mode, once per move this client itself committed (see
   *   `execute`'s `fromNetwork` param and `applyRemoteMove`).
   */
  constructor(view, options, hooks = {}) {
    this.view = view;
    this.options = { mode: 'ai', difficulty: 'knight', playerColor: 'w', ...options };
    this.hooks = hooks;

    this.game = new Chess();
    this.board = new Board();
    this.animator = new Animator(view.scene, {
      onSound: (cue, opts) => hooks.onSound?.(cue, opts)
    });

    this.pieces = new Map(); // piece id -> THREE.Group
    this.selected = null;
    this.legalForSelected = [];
    this.captured = { w: [], b: [] };
    this.finished = false;
    // Set once the result has been filed with the backend. Separate from
    // `finished` because a game can end several ways — checkmate, walking
    // out, an opponent walking out — and each must be reported exactly once
    // (see reportResult in main.js).
    this.reported = false;
    this.pendingPromotion = null;
    this.aiTimer = null;

    view.scene.add(this.board.group);
    // Dealt here and never again: `rebuild` respawns every mesh, so a helmet
    // rolled at build time would change on every undo.
    this.helmets = dealHelmets(this.game);
    this.spawnPieces();

    view.onClick = (raycaster) => this.handleClick(raycaster);
    view.faceSide(this.playerSide, true);

    this.emitTurn();
    // If the player took the dark side, the computer opens the game.
    this.maybeAiMove();
  }

  /** The colour the local player controls (both, in hotseat). */
  get playerSide() {
    return this.options.mode === 'hotseat' ? this.game.turn : this.options.playerColor;
  }

  isPlayerTurn() {
    return this.options.mode === 'hotseat' || this.game.turn === this.options.playerColor;
  }

  /* ---------------- setup ---------------- */

  spawnPieces() {
    for (let i = 0; i < 64; i++) {
      const piece = this.game.board[i];
      if (!piece) continue;
      const mesh = createPiece(piece.type, piece.color,
                               this.helmets.get(piece.origin) ?? 0);
      mesh.position.copy(squareToWorld(i));
      mesh.userData.square = i;
      mesh.userData.pieceId = piece.id;
      this.view.scene.add(mesh);
      this.pieces.set(piece.id, mesh);
    }
  }

  meshAt(square) {
    const piece = this.game.board[square];
    return piece ? this.pieces.get(piece.id) : null;
  }

  dispose() {
    this.cancelAiMove();
    this.animator.clear();
    this.view.onClick = null;
    for (const mesh of this.pieces.values()) this.view.scene.remove(mesh);
    this.pieces.clear();
    this.view.scene.remove(this.board.group);
    this.board.clearOverlays();
  }

  /* ---------------- input ---------------- */

  handleClick(raycaster) {
    if (this.finished || this.animator.busy || this.pendingPromotion) return;
    if (!this.isPlayerTurn()) return;

    // Piece meshes are irregular, so test them first and fall back to tiles.
    const pieceHits = raycaster.intersectObjects([...this.pieces.values()], true);
    let square = null;

    if (pieceHits.length) {
      let node = pieceHits[0].object;
      while (node && node.userData.pieceId === undefined) node = node.parent;
      if (node) square = node.userData.square;
    }
    if (square == null) {
      const tileHits = raycaster.intersectObjects(this.board.pickTargets, false);
      if (tileHits.length) square = tileHits[0].object.userData.square;
    }
    if (square == null) return;

    this.selectOrMove(square);
  }

  selectOrMove(square) {
    const move = this.legalForSelected.find((m) => m.to === square);
    if (move) {
      // Fired here, at the click itself — distinct from the 'move' cue,
      // which only plays once the piece has actually arrived (see execute()).
      const mover = this.game.board[this.selected];
      this.hooks.onSound?.('confirm', {
        sample: 'confirm',
        pool: mover ? POOL_BY_TYPE[mover.type] : 'male'
      });
      this.commit(move);
      return;
    }

    const piece = this.game.board[square];
    if (piece && piece.color === this.game.turn) {
      this.select(square);
    } else {
      this.clearSelection();
    }
  }

  select(square) {
    this.clearSelection();
    this.selected = square;
    this.legalForSelected = this.game.movesFrom(square);
    this.board.addOverlay(square, 'select');
    for (const move of this.legalForSelected) {
      this.board.addOverlay(move.to, move.captured || move.enPassant ? 'capture' : 'move');
    }
    const piece = this.game.board[square];
    this.hooks.onSound?.('select', {
      sample: 'select',
      pool: piece ? POOL_BY_TYPE[piece.type] : 'male'
    });
  }

  clearSelection() {
    if (this.selected != null) this.board.clearOverlay(this.selected);
    for (const move of this.legalForSelected) this.board.clearOverlay(move.to);
    this.selected = null;
    this.legalForSelected = [];
    this.markCheck();
    this.markLastMove();
  }

  /* ---------------- moving ---------------- */

  /** Routes a chosen move, pausing for a promotion choice when needed. */
  commit(move) {
    if (move.promotion) {
      // All four promotion variants share from/to; ask which one to play.
      // These come from the game rather than the current selection so a move
      // committed programmatically (a test, a replay, a networked opponent)
      // still gets the full set of choices.
      this.pendingPromotion = this.game
        .legalMoves()
        .filter((m) => m.from === move.from && m.to === move.to && m.promotion);
      this.clearSelection();
      this.hooks.onPromotion?.((choice) => {
        const picked = this.pendingPromotion.find((m) => m.promotion === choice)
          ?? this.pendingPromotion[0];
        this.pendingPromotion = null;
        this.execute(picked);
      });
      return;
    }
    this.execute(move);
  }

  /**
   * `fromNetwork` distinguishes a move this client is replaying (received
   * over the wire, already legal by construction — see `applyRemoteMove`)
   * from one it just committed itself, which is the one case `onLocalMove`
   * fires for: a 'remote' game needs to know exactly once per move "this is
   * mine, send it," not on every move including the opponent's echo.
   */
  async execute(move, { fromNetwork = false } = {}) {
    if (!move) return;
    this.clearSelection();
    this.board.clearOverlays();

    const mover = this.meshAt(move.from);
    const victimSquare = move.enPassant ? move.capturedSquare : move.to;
    const victim = move.captured ? this.meshAt(victimSquare) : null;

    const san = this.game.move(move);

    if (victim) {
      // Every piece not in this fight turns to watch it.
      const onlookers = [...this.pieces.values()].filter((p) => p !== mover && p !== victim);
      await this.animator.battle(mover, victim, move.from, move.to, victimSquare, { onlookers });
      this.view.scene.remove(victim);
      this.pieces.delete(move.captured.id);
      this.captured[move.captured.color].push(move.captured);
      this.hooks.onCapture?.({
        color: move.captured.color,
        glyph: GLYPHS[move.captured.color][move.captured.type]
      });
    } else {
      await this.animator.moveTo(mover, move.from, move.to);
      this.hooks.onSound?.('move');
    }

    if (move.castle) {
      const rook = this.pieces.get(
        this.game.board[move.rookTo]?.id ?? ''
      );
      if (rook) {
        await this.animator.slideTo(rook, move.rookFrom, move.rookTo);
        rook.userData.square = move.rookTo;
      }
    }

    mover.position.copy(squareToWorld(move.to));
    mover.userData.square = move.to;
    resetPose(mover);

    if (move.promotion) this.swapPromoted(move);

    this.lastMove = move;
    this.hooks.onMove?.({ san, move, turn: this.game.turn });
    if (this.options.mode === 'remote' && !fromNetwork) {
      this.hooks.onLocalMove?.({ from: move.from, to: move.to, promotion: move.promotion });
    }
    this.markLastMove();
    this.markCheck();

    if (this.checkEnd()) return;
    this.emitTurn();
    this.maybeAiMove({ afterCapture: !!victim });
  }

  /**
   * Applies a move received from the network (a 'remote' opponent's turn).
   * The wire only ever carries `{from, to, promotion}` — re-derived into a
   * full move object here by matching it against this client's own
   * `legalMoves()`, the same re-legalization `rebuild()` already does for
   * undo/replay. A move that doesn't match anything legal is refused rather
   * than executed: the far side already chose which promotion piece to
   * become, so this never goes through `commit()`'s promotion picker, which
   * exists only to ask the *local* player that question.
   */
  applyRemoteMove({ from, to, promotion }) {
    if (this.finished || this.pendingPromotion) return false;
    const move = this.game.legalMoves()
      .find((m) => m.from === from && m.to === to && m.promotion === promotion);
    if (!move) return false;
    this.execute(move, { fromNetwork: true });
    return true;
  }

  /** Replaces a promoted pawn's mesh with its new rank, keeping the piece id. */
  swapPromoted(move) {
    const piece = this.game.board[move.to];
    const old = this.pieces.get(piece.id);
    if (old) this.view.scene.remove(old);

    const mesh = createPiece(piece.type, piece.color);
    mesh.position.copy(squareToWorld(move.to));
    mesh.userData.square = move.to;
    mesh.userData.pieceId = piece.id;
    // A brief flourish so the transformation reads on screen.
    mesh.scale.setScalar(0.1);
    this.view.scene.add(mesh);
    this.pieces.set(piece.id, mesh);
    const grow = { t: 0 };
    const tick = () => {
      grow.t += 0.06;
      mesh.scale.setScalar(Math.min(1, grow.t < 1 ? 1 - Math.pow(1 - grow.t, 3) : 1));
      if (grow.t < 1) requestAnimationFrame(tick);
    };
    tick();
    this.hooks.onSound?.('swell');
  }

  markLastMove() {
    this.board.clearOverlays('last');
    if (!this.lastMove) return;
    this.board.addOverlay(this.lastMove.from, 'last');
    this.board.addOverlay(this.lastMove.to, 'last');
  }

  markCheck() {
    this.board.clearOverlays('check');
    if (this.game.inCheck()) {
      const king = this.game.kingSquare(this.game.turn);
      if (king >= 0) this.board.addOverlay(king, 'check');
    }
  }

  emitTurn() {
    const inCheck = this.game.inCheck();
    if (inCheck) this.hooks.onSound?.('check');
    this.hooks.onTurn?.({
      turn: this.game.turn,
      inCheck,
      yours: this.isPlayerTurn()
    });
    if (this.options.mode === 'hotseat') this.view.faceSide(this.game.turn);
  }

  checkEnd() {
    const status = this.game.status();
    if (!status) return false;
    this.finished = true;
    this.board.clearOverlays();

    const loser = this.game.turn;
    const winner = other(loser);
    let result = 'draw';
    if (status === 'checkmate') {
      result = this.options.mode === 'hotseat' ? 'win'
        : winner === this.options.playerColor ? 'win' : 'loss';
    }
    this.hooks.onSound?.(result === 'loss' ? 'lose' : 'win');
    this.hooks.onStatus?.({ status, winner: status === 'checkmate' ? winner : null, result });
    return true;
  }

  /**
   * Lets the AI reply once the player's animation has settled.
   *
   * The handle is kept so the reply can be cancelled: without it a pending
   * move outlives `dispose()` and `rebuild()`, searches a board that no longer
   * exists and calls `execute` on a dead session.
   */
  maybeAiMove({ afterCapture = false } = {}) {
    if (this.options.mode !== 'ai' || this.finished) return;
    if (this.game.turn === this.options.playerColor) return;

    // Deferred so the search never blocks the move animation. A capture has
    // just held the board for ten seconds, so it needs no further pause.
    this.cancelAiMove();
    this.aiTimer = setTimeout(() => {
      this.aiTimer = null;
      if (this.finished || this.pendingPromotion) return;
      const move = chooseMove(this.game, this.options.difficulty);
      if (move) this.execute(move);
    }, afterCapture ? 150 : 380);
  }

  cancelAiMove() {
    if (this.aiTimer === null || this.aiTimer === undefined) return;
    clearTimeout(this.aiTimer);
    this.aiTimer = null;
  }

  /**
   * Takes back the last move — and the AI's reply too, so control returns to
   * the player. Rebuilding the meshes is simpler and safer than reversing an
   * arbitrary animation mid-flight.
   */
  undo() {
    if (this.animator.busy || this.finished) return false;
    // Undoing only the local ply would desync from the opponent's
    // authoritative history — there is no correct amount to undo in a
    // networked game, so it is disabled outright rather than guessed at.
    if (this.options.mode === 'remote') return false;
    const backBy = this.options.mode === 'ai' ? 2 : 1;
    const history = this.game.history;
    if (history.length < backBy) return false;

    const replay = history.slice(0, history.length - backBy);
    this.rebuild(replay);
    return true;
  }

  /** Replays a move list onto a fresh board and respawns every mesh. */
  rebuild(moves) {
    // A reply already in flight would land on the position we are replacing.
    this.cancelAiMove();
    // Also drops any wreckage still lying on the board: taking a capture back
    // should take back what it broke.
    this.animator.clear();
    for (const mesh of this.pieces.values()) this.view.scene.remove(mesh);
    this.pieces.clear();
    this.board.clearOverlays();
    this.captured = { w: [], b: [] };

    this.game = new Chess();
    for (const recorded of moves) {
      const move = this.game.legalMoves().find(
        (m) => m.from === recorded.from && m.to === recorded.to &&
          m.promotion === recorded.promotion
      );
      if (!move) break;
      if (move.captured) this.captured[move.captured.color].push(move.captured);
      this.game.move(move);
    }

    this.spawnPieces();
    this.lastMove = this.game.history[this.game.history.length - 1] ?? null;
    this.markLastMove();
    this.markCheck();
    this.hooks.onRebuild?.({
      history: this.game.history.map((h) => h.san),
      captured: {
        w: this.captured.w.map((p) => GLYPHS.w[p.type]),
        b: this.captured.b.map((p) => GLYPHS.b[p.type])
      }
    });
    this.emitTurn();
  }

  update(dt, elapsed) {
    this.animator.update(dt);
    this.board.update(elapsed);
  }
}
